import { useState, useCallback, useEffect, useRef } from "react";

const useScreenSize = () => {
    const [screenSize, setScreenSize] = useState(getScreenSize());
    const mounted = useRef(true);
    const onSizeChanged = useCallback(() => {
        if (mounted.current) {
            setScreenSize(getScreenSize());
        }
    }, []);

    useEffect(() => {
        if (mounted.current) {
            subscribe(onSizeChanged);
        }

        return () => {
            mounted.current = false;
            unsubscribe(onSizeChanged);
        };
    }, [onSizeChanged]);
    return screenSize;
};

export const useScreenSizeClass = () => {
    const screenSize = useScreenSize();

    if (screenSize.isLarge) {
        return "screen-large";
    }

    if (screenSize.isMedium) {
        return "screen-medium";
    }

    if (screenSize.isSmall) {
        return "screen-small";
    }

    return "screen-x-small";
};

let handlers: (() => void)[] = [];

/**
 * Check if window is defined and window.matchMedia is supported.
 *
 * @returns True if window is defined and window.matchMedia is supported.
 */
const matchMediaDefined = () =>
    typeof window !== "undefined" && !!window.matchMedia;

let xSmallMedia: MediaQueryList | undefined = undefined;
let smallMedia: MediaQueryList | undefined = undefined;
let mediumMedia: MediaQueryList | undefined = undefined;
let largeMedia: MediaQueryList | undefined = undefined;

if (matchMediaDefined()) {
    xSmallMedia = window.matchMedia("(max-width: 599.99px)");
    smallMedia = window.matchMedia(
        "(min-width: 600px) and (max-width: 959.99px)",
    );
    mediumMedia = window.matchMedia(
        "(min-width: 960px) and (max-width: 1279.99px)",
    );
    largeMedia = window.matchMedia("(min-width: 1280px)");

    [xSmallMedia, smallMedia, mediumMedia, largeMedia].forEach((media) => {
        if (!media) {
            return;
        }

        media.addEventListener("change", (e) => {
            if (e.matches) {
                for (const handler of handlers) {
                    handler();
                }
            }
        });
    });
}

const subscribe = (handler: () => void) => handlers.push(handler);

const unsubscribe = (handler: () => void) => {
    handlers = handlers.filter((item) => item !== handler);
};

function getScreenSize() {
    if (!xSmallMedia || !smallMedia || !mediumMedia || !largeMedia) {
        return {
            isXSmall: true,
            isSmall: false,
            isMedium: false,
            isLarge: false,
        };
    }

    return {
        isXSmall: xSmallMedia.matches,
        isSmall: smallMedia.matches,
        isMedium: mediumMedia.matches,
        isLarge: largeMedia.matches,
    };
}
